import { ITranslationSchema } from '../type';

const translation: ITranslationSchema = {
  auth: {
    accountVerification: {
      code: {
        sent: "We just sent a code to {{to}}. Type it in here so we know it's you!",
        title: 'Check your messages',
      },
    },
    common: {
      emailLabel: 'Your Email',
      emailPlaceholder: 'Enter your email',
      errors: {
        confirmationCodeMissing: 'Please enter your code',
        emailAlreadyExists: 'An account with this email already exists. Please try signing in instead.',
        emailDomainNotAllowed: 'This email address cannot be used',
        emailInvalid: "We couldn't read this email. Check it looks like name@email.com",
        emailRequired: 'Please enter your email',
        invalidCredentials: 'Incorrect username or password',
        missingPasswordFields: 'Please fill in both password boxes',
        mobileNumberRequired: 'Please enter your mobile number',
        passwordConfirmationMissing: 'Please type your password again',
        passwordFormatDescription:
          'At least 8 characters, 1 number, 1 capital letter and 1 special character (like ! or ?)',
        passwordRequired: 'Please enter your password',
        passwordTooShort: 'Password is too short',
        passwordsDoNotMatch: "Passwords don't match. Please check again",
        phoneNumberCountryCodeRequired: 'Phone number must include country code (e.g. +44 for UK)',
        phoneNumberInvalid: 'Please enter a valid UK mobile number',
        phoneNumberRequired: 'Please enter your phone number',
        resetPasswordFailed: 'Failed to reset password. Please try again.',
        serverError: 'Server error. Please try again later.',
        usernameInvalid:
          'Please enter a valid email (name@email.com) or mobile number including country code (e.g. +44 for UK)',
        usernameRequired: 'Please enter your username (email or mobile number)',
      },
      mfa: {
        codeDescription: 'We sent a code to your registered mobile number',
        errors: {
          mfaVerificationFailed: 'We could not verify your code. Please check it and try again, or request a new one.',
          resendCodeFailed: 'Failed to resend code. Please try again.',
        },
      },
      mobileLabel: 'Your Phone Number',
      mobilePlaceholder: 'Enter your mobile number',
      passwordLabel: 'Password',
      passwordPlaceholder: 'Enter your password',
      passwordRepeatPlaceholder: 'Repeat password',
      repeatPasswordLabel: 'Repeat Password',
      showPassword: 'show',
      usernameLabel: 'Your Email or Phone Number',
      usernamePlaceholder: 'e.g. name@example.com or +447777112233',
    },
    confirmForgotPassword: {
      codeLabel: 'Verification code',
      description:
        'If the details you provided match an account, we have sent a verification code to either your email address, or to your mobile phone if you have one registered. Enter it below, then choose a new password for your account.',
      newPasswordLabel: 'Password',
      repeatPasswordLabel: 'Confirm your password',
      resendCode: {
        didntReceive: "Didn't receive a code?",
        resendButton: 'Resend it now',
      },
      resetPasswordButton: 'Reset Password',
      success: {
        message: 'Password reset successful.',
      },
      title: 'Forgotten your password?',
    },
    forgotPassword: {
      description:
        "Enter the email or phone number associated with your account and we'll send you a code to reset your password.",
      title: 'Forgotten your password?',
    },
    forgotPasswordSuccess: {
      description: 'Use the new password now to sign in to your account',
      returnButton: 'Return to sign in',
      title: 'Your password has been updated.',
    },
    login: {
      crisisCheck: {
        title: 'Safety Check',
        description:
          "This service isn't designed to be used in an emergency. <br/><br/> If you need support right now, or at anytime, please tap the Urgent Help button at the top of the page for contact information.",
      },
      forgotPasswordLinkText: 'Forgotten your password?',
      mfa: {
        codeInputLabel: 'Enter it below to log in',
        submit: 'Continue',
        title: "We need to confirm it's you",
      },
      signInButtonText: 'Sign in',
      title: 'Log In',
      welcomeHeader: 'Welcome',
    },
    register: {
      agreeTermsAndConditionsText: 'I agree to the Terms of Use and Privacy Policy.',
      alreadyHaveAccountSubHeader: 'Already have an account? <signInLink/>',
      errors: {
        confirmNotInCrisis: 'You must confirm you are not in crisis.',
        confirmTAndC: 'You must accept the terms and conditions.',
        missingRequiredFields: 'Please fill in all required fields.',
        requestFailed: 'Registration failed. Please try again.',
        termsNotAccepted: 'Please accept the terms and conditions to continue.',
        validationFailed: 'Please check your information and try again.',
      },
      mainHeader: 'Create your account to start the questionnaire',
      mfa: {
        selfReferralCodeInputLabel: 'Enter it below',
        codeInputLabel: 'Enter it below to create your account',
      },
      notInCrisisText:
        'I am not in crisis and understand this service does not offer immediate support. <urgentHelpLink>{{urgentHelp}}</urgentHelpLink> or the button at the top of the screen',
      notInCrisisUrgentHelpText: 'For Urgent Help, click here',
      passwordLess: {
        switchToEmailUsernameLabel: 'Your Email',
        switchToMobileUsernameLabel: 'Your Mobile',
        header: 'Start your referral',
        subHeader: "We'll send a verification code to confirm it's you.",
      },
      referralPortal: {
        mainHeader: 'Create your account',
        submit: 'Register',
      },
      startSurveyButtonText: 'Start Questionnaire',
      switchToEmailUsernameLabel: 'Sign up with Email',
      switchToMobileUsernameLabel: 'Sign up with Mobile',
      termsAndPrivacy: {
        agreeText:
          'I agree to the <termsLink>{{termsOfUse}}</termsLink> and <privacyLink>{{privacyPolicy}}</privacyLink>.',
        privacyPolicy: 'Privacy Policy',
        termsOfUse: 'Terms of Use',
      },
      title: 'Create Account',
    },
  },
  common: {
    buttons: {
      back: 'Back',
      backToMyAccount: 'My Account',
      cancel: 'Cancel',
      close: 'Close',
      continue: 'Continue',
      copied: 'Copied',
      copy: 'Copy',
      copyLink: 'Copy link',
      edit: 'Edit',
      goBack: 'Go back',
      hide: 'Hide',
      home: 'Home Screen',
      next: 'Next',
      ok: 'OK',
      send: 'Send',
      show: 'Show',
      submit: 'Submit',
      survey: 'Survey',
      view: 'View',
    },
    errors: {
      generic: 'Something went wrong, please try again. If the issue persists, please contact {{supportEmail}}.',
      networkError: 'Network error. Please check your connection and try again.',
      required: 'This field is required',
      notFound: 'Page not found',
    },
    table: {
      actions: 'Actions',
    },
  },
  date: {
    long: '{{date, datetime(dateStyle:full)}}',
    short: '{{date, datetime}}',
  },
  datetime: {
    long: '{{date, datetime(dateStyle:full; timeStyle:short)}}',
    short: '{{date, datetime(dateStyle:short; timeStyle: short)}}',
  },
  model: {
    common: {
      'created-at': 'Created At',
      publicId: 'Public ID',
      respondentName: 'Respondent Name',
      respondentType: {
        carer: 'Carer',
        informant1: 'Informant (1)',
        patient: 'Patient',
      },
      respondentTypeLabel: 'Respondent Type',
      serviceUserLink: 'Link',
      status: 'Status',
    },
    invite: {
      status: {
        cancelled: 'Cancelled',
        completed: 'Completed',
        expired: 'Expired',
        pending: 'Pending',
      },
    },
    referralSession: {
      status: {
        cancelled: 'Cancelled',
        completed: 'Completed',
        expired: 'Expired',
        pending: 'Pending',
      },
    },
    referrals: {
      'completed-at': 'Completed At',
      'completion-date': 'Completion Date',
      name: 'Name',
      'patient-pi-system-id': 'ID',
      'referral-date': 'Referral Date',
      service: 'Service',
      status: {
        cancelled: 'Cancelled',
        completed: 'Completed',
        expired: 'Expired',
        pending: 'Pending',
      },
    },
  },
  navigation: {
    back: 'Back',
    home: 'Home',
  },
  page: {
    about: {
      howItWorks: {
        description:
          'This app uses standardised questionnaires to help collect information about your symptoms which will be reviewed by a Healthcare Professional',
        title: '',
      },
      loadingState: 'Getting app info...',
      noData: 'Application information not found!',
      softwareInfo: {
        appName: 'App name',
        appVersion: 'App version',
        manufacturer: 'Manufacturer or maintainer',
        title: 'Software information',
      },
      title: 'About this app',
    },
    account: {
      aboutLink: 'About this app',
      changePassword: 'Change Password',
      changePasswordForm: {
        currentPassword: 'Current Password',
        newPassword: 'New Password',
        passwordRequirements: {
          lowercase: 'One lowercase character',
          minLength: 'At least 8 characters',
          numeric: 'One numeric character',
          symbol: 'One symbol character (! # % & $ @)',
          title: 'Your password must contain:',
          uppercase: 'One uppercase character',
        },
        repeatPassword: 'Repeat New Password',
      },
      changePasswordSuccess: {
        button: 'My Account',
        title: 'Password Successfully Changed!',
      },
      loadingState: 'Getting user details....',
      privacyLink: 'Privacy policy',
      signOut: 'Sign Out',
      support: 'If you need help with your account settings, please contact <linkTag />.',
      termsLink: 'Terms of use',
      title: 'My Account',
      userNotFound: 'User not found',
    },
    activityDashboard: {
      accountCreatedCard: {
        title: 'Account Created',
      },
      cancelledCard: {
        title: 'Cancelled',
      },
      completionRateCard: {
        title: 'Completion Rate',
      },
      expiredCard: {
        title: 'Expired',
      },
      invitesCard: {
        title: 'Invites',
      },
      referralDateLabel: 'Referral Date Between',
      reportsCompletedCard: {
        title: 'Reports Completed',
      },
      title: 'Activity Dashboard',
    },
    clinicianHomepage: {
      disclaimer: {
        text: 'All access to patient records is monitored. Unauthorised access may lead to disciplinary or legal action.',
      },
      latestActivity: {
        buttonText: 'View All Activity',
        completionRateCard: {
          subtext: 'In the past {{days}} days',
          title: 'Completion Rate',
        },
        invitesCard: {
          subtext: 'In the past {{days}} days',
          title: 'Invites',
        },
        reportsCompletedCard: {
          subtext: 'In the past {{days}} days',
          title: 'Reports Completed',
        },
        title: 'Latest Activity',
      },
      latestCompletions: {
        buttonText: 'View All Reports',
        title: 'Latest Completions',
      },
      menu: {
        activityDashboard: 'Activity Dashboard',
        clinicalReports: 'Clinical Reports',
        instructionsForUse: 'Instructions for Use',
        referralUploadJobs: 'Bulk Upload Referrals',
        signOut: 'Sign Out',
      },
      title: 'Welcome back',
    },
    healthLibrary: {
      bannerText:
        "🔗 External links are bold and underlined and opens outside beseen. The “Need Urgent Help” button may not be available on the new page. Use your browser's back button or close the tab to return.",
      errors: {
        notFound: 'That article could not be found',
      },
      filterTitle: 'Filter topics',
      otherTopicTitle: 'Other',
      subtitle: 'Your space to read and learn about mental health and ND topics.',
      subtitle2:
        'This content is general health information intended to support your understanding. It has not been tailored and is not a substitute for clinical advice.',
      subtitle3: 'If anything you read raises questions or concerns, please discuss them with your care team.',
      title: 'Health Library',
    },
    home: {
      cardMessage: {
        cancelled: 'Cancelled',
        completed: 'Completed',
        contactSupport: 'Contact <supportLink/> for more information',
        expired: 'Expired',
        inProgress: 'Started',
        notStarted: 'Not Started',
        sent: 'Sent',
      },
      checkIns: {
        header: 'Your Check-ins',
        info: 'Here you can find all of your questionnaires',
      },
      greeting: 'Hi',
      healthLibraryCard: {
        badge: 'NEW',
        description: 'Learn more about common symptoms and coping mechanisms',
      },
      loadingState: 'Loading referral details',
      noActiveReferral: {
        subtitle:
          'Please use the link in your referral, or return to the self-referral link to continue. <br/><br/> If you need help, get in touch at <linkTag/>',
        title: "We couldn't find an active referral",
      },
      questionnaires: {
        header: 'Your Questionnaires',
      },
    },
    mfaSetup: {
      phone: {
        alreadyHaveCode: 'Already have a code? <enterCodeLink />',
        description: "To keep your data private and secure, we'll need to send a one-time code to your mobile phone.",
        enterCodeHere: 'Enter it here',
        label: 'Please enter your mobile number',
        placeholder: 'Mobile number',
        submit: 'Send code',
        title: 'Secure your account',
      },
      code: {
        changeNumber: 'provide a different number',
        contactSupport: 'contact <linkTag /> for help',
        description: 'We sent a code to {{phoneNumber}}',
        helpPrefix: "Haven't received the message? <retryLink />",
        helpPrefixWithSecondLink: "Haven't received the message? <retryLink /> or <secondLink />",
        placeholder: 'Code from the message',
        startAgain: 'Start again',
        submit: 'Verify',
        title: 'Enter your code',
        tryAgain: 'Try again',
        tryAgainIn: 'Try again in {{seconds}} seconds',
      },
    },
    professionalReferral: {
      breadcrumb: {
        createReferralRequest: 'Create Referral Request',
        editReferralRequest: 'Edit Referral Request',
        questionnaire: 'Questionnaire',
      },
      home: {
        availableServices: 'Create New Referral',
        cancelDialog: {
          confirmationLabel: "Type '{{keyword}}' to confirm",
          description:
            'This will cancel every pending session for this referral request, and any outstanding respondent invites will be withdrawn. This cannot be undone.',
          error: 'Unable to cancel this referral request. It may have already been completed or cancelled.',
          keyword: 'CANCEL',
          title: 'Cancel referral request',
        },
        draftReferralRequests: 'Draft Referral Requests',
        draftsError: 'Unable to load your draft referral requests. Please try again later.',
        error: 'Unable to load your submitted referrals. Please try again later.',
        invites: {
          delayed: "This is taking longer than expected. If it doesn't appear soon, please contact support.",
          none: 'No invites for this referral',
          phoneNumber: 'Phone Number',
          preparing: 'Invites are being prepared',
        },
        submittedReferrals: 'Sent Referral Requests',
        table: {
          cancelReferral: 'Cancel referral request',
          delayed: 'Delayed',
          expiresAt: 'Expires At',
          notProvided: 'Not provided',
          organisation: 'Organisation',
          patientName: 'Patient Name',
          piSystemId: 'Patient Identifier',
          sending: 'Sending…',
          service: 'Service',
          status: 'Status',
          submittedAt: 'Submitted At',
        },
      },
      referralRequest: {
        familyNameLabel: 'Family name',
        givenNameLabel: 'Given name',
        patientIdLabel: 'Patient identifier',
        phoneNumberLabel: 'Phone number',
        saveAndContinueButton: 'Save and Continue',
      },
      submit: {
        title: 'Submit Referral',
        cautionText:
          'Please note: Invitations are sent straight away. Please double-check the recipient details and selected service before sending.',
        notReadyError: 'This referral request is not ready to be sent. Please go back and check its details.',
        errors: {
          contactIncomplete:
            'Respondent contact details are incomplete. Please provide a given name, family name and phone number for each respondent.',
          missingPatientId: 'A patient identifier is required.',
          noPhoneNumber: 'At least one respondent needs a phone number so they can be invited.',
          patientIncomplete: 'Some required patient details are missing.',
          surveyIncomplete: 'The referrer questionnaire has not been completed.',
        },
      },
      welcome: {
        fallbackText: 'Start your professional referral registration flow.',
        header: 'Welcome',
      },
    },
    questionnaireEnd: {
      header1: 'Thank you for submitting your questionnaire!',
      header2: 'Need to talk to someone now?',
      header3: 'Need Urgent Support?',
      paragraph1: 'Your answers have been sent to the care team.',
      paragraph2:
        "These services offer confidential support from trained volunteers. You can talk about anything that's troubling you, no matter how difficult, and they will only share your information if they are very worried about you or think you are in immediate danger:",
      supportServices:
        '<ul><li>• Call 116 123 to talk to <samaritanLink/>, or email: jo@samaritans.org for a reply within 24 hours</li><li>• Text "SHOUT" to 85258 to contact the <shoutLink/>, or text "YM" if you\'re under 19</li><li>• If you\'re under 19, you can also call 0800 1111 to talk to <childlineLink/>. The number will not appear on your phone bill.</li></ul>',
      urgentSupport:
        "<ul><li>• Get help from <nhs111Link/> or call 111 and select the mental health option. Alternatively, request an urgent GP appointment</li><li>• Call 999 or go to A&E now if:<ul><li>• Someone's life is at risk – for example, they have seriously injured themselves or taken an overdose.</li><li>• You do not feel you can keep yourself or someone else safe.</li></ul></li></ul>",
    },
    referralRequestSurvey: {
      errors: {
        notFound: 'Sorry, we could not find that survey.',
        conflict: 'This survey was updated elsewhere. Please <refreshLink /> to see the latest version.',
      },
    },
    referralUploadJobs: {
      chooseFile: 'Choose file',
      erroredRows: {
        eprId: 'EPR ID',
        error: 'Error',
        name: 'Name',
        none: 'No errored rows for this upload',
        rowNumber: 'Row',
      },
      errors: {
        duplicateColumns: 'Duplicate column(s) found: {{columns}}',
        duplicateReferralRow:
          'Row {{rowNumber}}: the EPR ID {{eprId}} and Service {{service}} appear more than once in the file',
        emptyColumnHeader: 'The file contains a blank column header',
        fieldTooLongRow: 'Row {{rowNumber}}: {{columns}} must be {{max}} characters or fewer',
        fileNameTooLong: 'The file name must be {{max}} characters or fewer',
        fileReadError: 'The file could not be read. Please try again.',
        fileTooLarge: 'The file is too large; the maximum size is {{maxSizeMb}}MB',
        genericFailure: 'Something went wrong uploading the file. Please try again.',
        incompleteCarerRow: 'Row {{rowNumber}}: carer information is incomplete, missing {{columns}}',
        incompleteInformant1Row: 'Row {{rowNumber}}: informant information is incomplete, missing {{columns}}',
        incompleteRow: 'Row {{rowNumber}} is missing: {{columns}}',
        invalidPhoneNumberRow: 'Row {{rowNumber}}: {{columns}} must be a valid mobile number',
        malformedCsv: 'Unable to parse file as CSV: {{reason}}',
        missingColumns: 'Missing required column(s): {{columns}}',
        noDataRows: 'File contains no data rows',
        tooManyRows: 'File contains too many rows ({{count}}); the maximum is {{max}}',
        unrecognisedColumns: 'Unrecognised column(s): {{columns}}',
        validationFailed:
          'The file could not be processed. Please check the required columns and data, then try again.',
      },
      noFileChosen: 'No file chosen',
      previousUploads: 'Previous uploads',
      services: 'Choose a service to upload referrals for',
      table: {
        complete: 'Complete',
        errored: 'Errored',
        fileName: 'File Name',
        pending: 'Pending',
        rowCount: 'Rows',
        service: 'Service',
        uploadedAt: 'Uploaded At',
        uploadedBy: 'Uploaded By',
      },
      title: 'Bulk Upload Referrals',
      upload: 'Upload',
      uploadSuccessOne: '{{rowCount}} row was uploaded and is pending processing.',
      uploadSuccessOther: '{{rowCount}} rows were uploaded and are pending processing.',
      uploadSuccessTitle: 'Upload complete',
      uploadTitle: 'Bulk Upload Referrals: {{serviceDescription}}',
    },
    referrals: {
      invites: {
        none: 'No respondents for this referral',
      },
      report: 'Report',
      title: 'Clinical Reports',
      'view-report': 'View Report',
    },
    report: {
      errors: {
        InviteExpired: {
          subtitle: 'That means that they have been referred, but their invite expired before it was completed.',
          title: "This service user doesn't have a beseen report, or an active referral.",
        },
        InviteNotFound: {
          subtitle:
            "That means that they haven't been asked to fill in the questionnaire and a beseen report won't be available.",
          title: 'This service user has not been referred to beseen',
        },
        ReferralCancelled: {
          subtitle:
            'That means that they have been referred, but they have not completed a questionnaire, and the questionnaire is no longer available.',
          title: "This service user doesn't have a beseen report, or an active referral.",
        },
        ReferralPending: {
          subtitle:
            "The questionnaire hasn't been completed by the service user. This report will appear once it's completed.",
          title: 'Report not yet available',
        },
        notFound: 'Sorry, we could not find that report',
      },
    },
    reportAdmin: {
      errors: {
        noReports: 'No reports',
        noSurveys: 'No surveys',
      },
    },
    smsOptOut: {
      header: "Please confirm you'd like to opt out our text reminders",
      confirmButton: 'Confirm',
      errors: {
        missingParameters: 'Invalid opt-out link. Please contact support.',
        generic: 'Unable to process your request. Please try again later.',
      },
    },
    smsOptOutSuccess: {
      header: "Success! You won't receive anymore reminders for this referral.",
    },
    viewReferral: {
      dataSafetyContent:
        'Psyomics collects and handles the data you provide securely, sensitively and privately. {{organisationName}} will receive your answers once you submit and will add your answers to your health record to allow your care provider to review them. To learn more about how we handle your data and keep it safe, please see our <0>Privacy Policy.</0>',
      dataSafetyTitle: 'Your data stays safe',
      errors: {
        notFound: 'Sorry, we could not find that referral',
      },
      fillingChoice: 'Filling it out is your choice',
      fillingHelpNote: 'but it really helps us help you.',
      introText:
        'This questionnaire helps the {{organisationName}} team understand your needs and plan the best care for you.',
      linkExpired: {
        body: 'Please contact <linkTag />',
        header: 'This link is no longer working',
      },
      nextButton: 'Next',
      questionnaireInProgress: {
        body: "The new report will appear here once it's finished, but you can view the service user's previous report(s) here.",
        header: 'New questionnaire in progress',
      },
      welcomeMessage: 'Welcome {{name}},',
      whatToExpect1: 'Takes about 40 minutes.',
      whatToExpect2: 'You can pause anytime — your progress is saved.',
      whatToExpect3: 'Questions cover your feelings, experiences, and daily life.',
      whatToExpectTitle: 'What to expect',
      whyComplete1: 'It may make your appointment more helpful.',
      whyComplete2: 'It may help us understand you better.',
      whyComplete3: 'It may support better decisions about your care.',
      whyCompleteTitle: 'Why complete it?',
    },
    viewSurvey: {
      errors: {
        attachmentValidationFailed:
          'One or more attachments could not be verified. Please <refreshLink /> to try again.',
        conflict: 'You made changes elsewhere, <refreshLink /> this tab to see the latest version.',
        notFound: 'Survey not found',
      },
    },
    welcome: {
      title: 'Welcome to beseen',
    },
  },
  report: {
    answerSkipped: 'Skipped',
    blocks: {
      attachments: {
        columnHeaderContentType: 'Content Type',
        columnHeaderCreatedAt: 'Uploaded',
        columnHeaderFileName: 'File Name',
        columnHeaderFileSize: 'File Size',
        columnHeaderRespondentType: 'Respondent Type',
      },
      exportPDFButton: {
        buttonText: 'Export PDF',
      },
      measureBreakdown: {
        appendixTitle: 'Score Calculation Appendix',
        clinicalReferenceLink: 'Clinical guidance and Academic references',
        noExpression: 'No expression selected',
        totalScore: 'Total score {{value}}/{{maxValue}}',
        validatedQuestionnaires: 'Further information about Validated Questionnaires',
        wrongExpression: 'Expression is not of correct type',
      },
      pdfCover: {
        reportIsReady: 'Report is ready for review',
        clickTheButton: 'Click the button to review interactive report (recommended)',
        orScroll: 'or scroll down to the PDF version',
        buttonText: 'View Report Now',
      },
      reportSwitcher: {
        currentReport: 'Current Report Completed',
        partialReport: 'Partial Report Updated',
        mostRecentLabel: '(most recent)',
        reportInfo: '{{mostRecent}}| {{index}} of {{total}}',
      },
      showCalculation: {
        showCalculation: 'Show Calculations',
      },
    },
    conditionNotAdministered: 'Not Administered',
    conditionNotShown: 'N/A',
    conditionSkipped: 'Skipped',
    questionNotCompleted: 'Not Completed',
    questionNotShown: 'Not asked',
  },
  'ui-components': {
    appBar: {
      openUserMenu: 'Open user menu',
      userMenu: 'User menu',
    },
    banner: {
      dismiss: 'Dismiss banner',
    },
    'demographics-bar': {
      demographic: {
        age: 'Age',
        'date-of-birth': 'Date of birth',
        eprIdLabel: 'EPR ID',
        ethnicity: 'Ethnicity',
        gender: 'Gender',
        name: 'Name',
        notFoundText: 'Not found',
        sex: 'Sex',
      },
      'patient-id-mismatch': {
        label: "NHS number provided doesn't match the NHS lookup",
        text: 'IDENTITY NOT VERIFIED',
      },
      incompletePlaceholder: 'Incomplete',
    },
    dropdownMenu: {
      noItemsAvailable: 'No items available',
      openMenu: 'Open menu',
    },
    genericError: {
      title: 'Apologies, we are experiencing technical difficulties.',
      subtitle: 'Please contact <linkTag /> for further help',
    },
    loggedOut: {
      title: "You've logged out",
      ssoMessage: "Next time you come back, you'll be signed in automatically through your work account.",
      goToLogin: 'Sign In',
    },
    patientAppBar: {
      crisisText: 'Urgent help',
    },
    privacyPolicy: {
      redirecting: 'Redirecting to Privacy Policy...',
    },
    showCalculation: {
      calculation: 'calculation',
    },
    survey: {
      attachments: {
        maxFilesExceeded: 'You can upload a maximum of {{max}} files for this question.',
        unsafeFile: 'This file could not be uploaded because it was identified as unsafe. Please choose another file.',
        scanUnavailable: "We couldn't verify this file at this time. Please try again.",
        uploadError: 'Upload failed. Please try again or contact support if the problem continues.',
      },
      toc: {
        title: 'Table of Contents',
      },
    },
    table: {
      filters: {
        filters: 'Filters',
        moreFilters: 'More filters',
        removeFilter: 'Remove {{type}} filter',
        saveFilters: 'Save Filters',
      },
      pagination: {
        label: 'Table pagination',
        nextPage: 'next page',
        of: 'of',
        page: 'Page',
        previousPage: 'previous page',
      },
    },
    termsAndConditions: {
      redirecting: 'Redirecting to Terms of Use...',
    },
    urgentHelpButton: {
      buttonText: 'Urgent Help',
      callHelp: 'NHS 111 → choose mental health (option 2), or contact your GP urgently',
      callSamaritans:
        'Samaritans 116 123 (free, 24/7, confidential) or text SHOUT to 85258. Under 19? Call Childline 0800 1111 (free, private)',
      callUrgentHelp:
        "Call 999 or go to A&E if you've seriously hurt yourself, taken an overdose, or can't stay safe right now",
      crisisTeamText: 'Already in mental health care? Contact your service if open, or use the above services.',
      emergencyTitle: 'EMERGENCY:',
      goToAE: 'Go to your nearest A&E or get someone to take you there',
      talkToSomeoneTitle: 'TALK TO SOMEONE:',
      title: 'Need support right now?',
      urgentHelpTitle: 'URGENT HELP:',
    },
  },
};
export default translation;
